import { faGlobe } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { observer } from "mobx-react-lite";
import React, { useContext, useState } from "react";
import { useDrag } from "react-dnd";
import styled from "styled-components";
import { GENERIC_COMPONENT } from "../Component";
import { ComponentItemProps, DefaultItem } from "../ComponentTree";
import { DragSpec, IEditableComponent } from "../EditableComponent";
import { NewItem } from "../NewComponents";
import { BarBoolProperty, BarTextProperty } from "../PropertiesBar";
import { PageContext } from "../../PageContext";
import { IframeSpec, IFRAME_COMPONENT, IframeAspectRatio } from "./Iframe";

const IframeInput = styled.div`
  margin-bottom: 15px;
`;

const SelectContainer = styled.div`
  margin-bottom: 15px;
`;

const SelectLabel = styled.div`
  margin-top: 8px;
  font-size: 13px;
  color: #adadad;
`;

const SelectInput = styled.select`
  background: #3a4348;
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 4px;
  outline: unset;
  width: calc(100% - 10px);
  :focus {
    box-shadow: #0080ff 0 0 6px 1px;
    outline: unset;
  }
`;

const IframeWrapper = styled.div<{
  aspectRatio: IframeAspectRatio;
  width: string;
  height: string;
  showBorder: boolean;
}>`
  width: ${props => props.width || "100%"};
  ${props => props.aspectRatio === "custom" ? `height: ${props.height || "500px"};` : ""};
  position: relative;
  border: ${props => props.showBorder ? "1px solid #ddd" : "none"};
  box-shadow: ${props => props.showBorder ? "0 2px 4px rgba(0,0,0,0.1)" : "none"};
  border-radius: ${props => props.showBorder ? "4px" : "0"};
  overflow: hidden;
  background-color: #f8f9fa;
  
  ${props => props.aspectRatio !== "custom" ? `
    padding-top: ${props.aspectRatio === "16:9" ? "56.25%" : 
                 props.aspectRatio === "4:3" ? "75%" : 
                 props.aspectRatio === "1:1" ? "100%" : "0"};
  ` : ""}
`;

const ResponsiveIframe = styled.iframe`
  position: ${props => props.style?.position || "absolute"};
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 100%;
  border: 0;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  width: 100%;
  background-color: #f8f9fa;
  color: #6c757d;
  border: 1px dashed #dee2e6;
  border-radius: 4px;
  font-size: 16px;
`;

export const EditComponent = observer((props: { spec: IframeSpec }) => {
  const { isEditing } = useContext(PageContext);
  const { url, aspectRatio, width, height, allowFullscreen, lazy, title, showBorder } = props.spec;

  if (!url && isEditing) {
    return <EmptyState>Bitte geben Sie eine URL in den Eigenschaften an.</EmptyState>;
  }

  if (!url) {
    return <EmptyState>Keine URL angegeben</EmptyState>;
  }

  const iframeProps = {
    src: url,
    title: title || "Embedded content",
    loading: lazy ? "lazy" : "eager",
    allowFullScreen: allowFullscreen,
    referrerPolicy: "no-referrer-when-downgrade" as const,
    style: {
      position: aspectRatio === "custom" ? "relative" : "absolute"
    }
  };

  return (
    <IframeWrapper 
      aspectRatio={aspectRatio} 
      width={width} 
      height={height} 
      showBorder={showBorder}
    >
      <ResponsiveIframe {...iframeProps} />
    </IframeWrapper>
  );
});

const Properties = observer((props: { spec: IframeSpec }) => {
  // Default values for new frames
  if (!props.spec.width) {
    props.spec.width = "100%";
  }
  
  if (!props.spec.height) {
    props.spec.height = "500px";
  }
  
  if (props.spec.aspectRatio === undefined) {
    props.spec.aspectRatio = "16:9";
  }

  return (
    <div>
      <IframeInput>
        <BarTextProperty
          name="URL"
          value={props.spec.url || ""}
          onChange={(newValue) => (props.spec.url = newValue)}
        />
      </IframeInput>
      
      <SelectContainer>
        <SelectLabel>Seitenverhältnis:</SelectLabel>
        <SelectInput
          value={props.spec.aspectRatio}
          onChange={(e) => props.spec.aspectRatio = e.target.value as IframeAspectRatio}
        >
          <option value="16:9">16:9 (Widescreen)</option>
          <option value="4:3">4:3 (Standard)</option>
          <option value="1:1">1:1 (Quadrat)</option>
          <option value="custom">Benutzerdefiniert</option>
        </SelectInput>
      </SelectContainer>
      
      {props.spec.aspectRatio === "custom" && (
        <>
          <IframeInput>
            <BarTextProperty
              name="Breite (z.B. 100% oder 600px)"
              value={props.spec.width || "100%"}
              onChange={(newValue) => (props.spec.width = newValue)}
            />
          </IframeInput>
          
          <IframeInput>
            <BarTextProperty
              name="Höhe (z.B. 500px)"
              value={props.spec.height || "500px"}
              onChange={(newValue) => (props.spec.height = newValue)}
            />
          </IframeInput>
        </>
      )}
      
      <IframeInput>
        <BarTextProperty
          name="Titel (für Barrierefreiheit)"
          value={props.spec.title || ""}
          onChange={(newValue) => (props.spec.title = newValue)}
        />
      </IframeInput>
      
      <BarBoolProperty
        name="Vollbildmodus erlauben"
        value={props.spec.allowFullscreen || false}
        onChange={(newValue) => (props.spec.allowFullscreen = newValue)}
      />
      
      <BarBoolProperty
        name="Verzögertes Laden (Lazy Loading)"
        value={props.spec.lazy || false}
        onChange={(newValue) => (props.spec.lazy = newValue)}
      />
      
      <BarBoolProperty
        name="Rahmen anzeigen"
        value={props.spec.showBorder || false}
        onChange={(newValue) => (props.spec.showBorder = newValue)}
      />
    </div>
  );
});

const Item = observer((props: ComponentItemProps<IframeSpec>) => {
  return <DefaultItem {...props} icon={faGlobe} name="iFrame" />;
});

const New = () => {
  const [, drag] = useDrag<DragSpec, unknown, unknown>(() => ({
    type: GENERIC_COMPONENT,
    item: {
      spec: {
        type: IFRAME_COMPONENT,
        url: "",
        aspectRatio: "16:9",
        width: "100%",
        height: "500px",
        allowFullscreen: true,
        lazy: true,
        title: "",
        showBorder: true
      },
    },
  }));
  return (
    <NewItem
      isActive={false}
      ref={(elem) => {
        drag(elem);
      }}
    >
      <FontAwesomeIcon icon={faGlobe} />
      <span> iFrame </span>
    </NewItem>
  );
};

export const IframeComponent: IEditableComponent<IframeSpec> = {
  component: EditComponent,
  properties: Properties,
  item: Item,
  new: New,
};
